import { type ReactElement } from 'react';
import cx from 'clsx';
import { SMALL_VARIANT_SIZES, STATUS_ICON_CONFIG } from './consts';
import type { StatusIconProps } from './types';
import styles from './status-icon.module.css';

export type { StatusIconProps, StatusIconSize, StatusIconStatus } from './types';
export { STATUS_ICON_SIZES, STATUS_ICON_STATUSES } from './consts';

/**
 * Displays a semantic status as a circular icon badge.
 *
 * The icon and background colour are spec-driven — consumers supply only the
 * semantic `status` value; the visual representation is handled automatically.
 *
 * @example
 * // Standalone — provide a label so assistive technology can read the status
 * <StatusIcon status="approved" aria-label="Approved" />
 *
 * // Decorative — omit the label when adjacent text already describes the status
 * <StatusIcon status="processing" size="16" />
 */
export function StatusIcon({
  status,
  size = '20',
  'aria-label': ariaLabel,
  'data-testid': testId,
}: StatusIconProps): ReactElement {
  const { Icon, IconSmall, color } = STATUS_ICON_CONFIG[status];
  const ResolvedIcon = SMALL_VARIANT_SIZES.has(size) ? IconSmall : Icon;

  return (
    <div
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
      className={cx(styles.statusIcon, styles[`statusIcon${size}`])}
      data-testid={testId}
      role={ariaLabel ? 'img' : undefined}
      style={{ color }}
    >
      <ResolvedIcon />
    </div>
  );
}
